import React, { useEffect } from "react";
import { Flex, TextField, Text, Button, Select, Message } from "../../../resourses/digitallabs/one-x-ui";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import "../../../styles/DynamicFormRenderer_Helix.css";
import { MAIN_STEPS } from "../../../constants/constants";
import DynamicSelectCountry from "./dynamicSelectCountry";

/* ---------------- COMPONENT REGISTRY ---------------- */
const FIELD_COMPONENTS = {
  input: TextField,
  input_medication: TextField,
  select: Select,
  select_medication: Select,
};

/* ---------------- CREATE EMPTY ITEM ---------------- */
const createEmptyItem = (field) => {
  const obj = { id: Date.now() + Math.random() };

  const fillFields = (fields) => {
    fields?.forEach((f) => {
      if (f.component === "composite") {
        fillFields(f.fields);
      } else {
        obj[f.name] = "";
      }
    });
  };

  fillFields(field.sub_fields);

  return obj;
};

/* ---------------- NORMALIZE SELECT ---------------- */
const normalizeSelectValue = (value) => {
  if (!value) return [];
  return [value];
};

/* ---------------- RULE PARSER ---------------- */
const getRuleValue = (rules = [], key, defaultValue) => {
  const rule = rules.find((r) => r.startsWith(`${key}:`));
  if (!rule) return defaultValue;

  const value = parseInt(rule.split(":")[1], 10);
  return isNaN(value) ? defaultValue : value;
};

/* ---------------- GENERIC FIELD RENDERER ---------------- */
const renderField = ({ fieldConfig, value, onChange, fieldIsRequired, extraProps }) => {
  /* ---------- COUNTRY COMPONENT ---------- */
  if (fieldConfig.component === "countrySelection") {
    return (
      <DynamicSelectCountry
        field={fieldConfig}
        fieldName={fieldConfig.name}
        value={value}
        updateValue={(name, val) => onChange(val)}
        {...extraProps}
      />
    );
  }

  const Component = FIELD_COMPONENTS[fieldConfig.component];
  if (!Component) return null;

  /* ---------- SELECT ---------- */
  if (fieldConfig.component === "select" || fieldConfig.component === "select_medication") {
    const options = fieldConfig.valid_answers || fieldConfig.options || [];

    return (
      <Component
        items={options.map((opt) => ({
          label: opt,
          value: opt,
        }))}
        value={normalizeSelectValue(value)}
        closeOnSelect
        valuePlaceholder={fieldConfig.placeholder || `Select ${fieldConfig.question || ""}`}
        size="md"
        label={{
          label: fieldConfig.question || "",
          optional: !fieldIsRequired(fieldConfig),
        }}
        onValueChange={(details) => {
          onChange(details.value?.[0] ?? "");
        }}
      />
    );
  }

  /* ---------- INPUT ---------- */
  return (
    <Component
      value={value || ""}
      placeholder={fieldConfig.placeholder || fieldConfig.question || "Enter"}
      variant="outline"
      maxLength={getRuleValue(fieldConfig.rules, "maxLength") || 25}
      size="md"
      label={{
        label: fieldConfig.question || "",
        optional: !fieldIsRequired(fieldConfig),
      }}
      onChange={(e) => {
        let val = e.target.value ?? "";

        if (fieldConfig.rules?.includes("numeric")) {
          val = val.replace(/[^0-9]/g, "");
        }

        if (val.length > 0) {
          val = val.charAt(0).toUpperCase() + val.slice(1);
        }

        onChange(val);
      }}
    />
  );
};

/* ======================================================= */
/* ================= MAIN COMPONENT ======================= */
/* ======================================================= */

const DynamicMultipleOptions = ({
  field,
  value = [],
  updateValue,
  fieldRefs,
  renderLabelWithPrefix,
  isMain,
  step,
  fieldIsRequired,

  // 👇 pass-through props for country component
  normalizeCountryOptions,
  validateSingleField,
  validateVisible,
  setErrors,
  setIsValid,
  onFormValidityChange,
  submitted,
  errors,
  countries_metaData,
}) => {
  const items = Array.isArray(value) ? value : [];
  console.log("errors for field", errors);

  const minItems = getRuleValue(field.rules, "min_items", 1);
  const maxItems = getRuleValue(field.rules, "max_items", 5);

  /* ---------------- ENSURE MIN ITEMS ---------------- */
  useEffect(() => {
    if (!items.length && minItems) {
      updateValue(
        field.name,
        Array.from({ length: minItems }, () => createEmptyItem(field))
      );
    }
    // eslint-disable-next-line
  }, []);

  /* ---------------- ADD ---------------- */
  const handleAdd = () => {
    if (items.length >= maxItems) return;
    updateValue(field.name, [...items, createEmptyItem(field)]);
  };

  /* ---------------- DELETE ---------------- */
  const handleDelete = (id) => {
    updateValue(
      field.name,
      items.filter((it) => it.id !== id)
    );
  };

  /* ---------------- UPDATE ---------------- */
  const updateItem = (id, key, val) => {
    const updated = items.map((it) => (it.id === id ? { ...it, [key]: val } : it));
    updateValue(field.name, updated);
  };

  const extraProps = {
    isMain,
    fieldRefs,
    normalizeCountryOptions,
    normalizeSelectValue,
    renderLabelWithPrefix,
    fieldIsRequired,
    validateSingleField,
    validateVisible,
    setErrors,
    setIsValid,
    onFormValidityChange,
    submitted,
    errors,
    countries_metaData,
  };

  return (
    <Flex direction="column" gap="md" ref={(el) => (fieldRefs.current[field.name] = el)}>
      {/* LABEL */}
      <Text className={isMain ? (step === MAIN_STEPS.PERSONAL_DETAILS ? "dfh-label-small" : "dfh-label") : "subquestion-label"}>
        {renderLabelWithPrefix(isMain, field.question, step)}
      </Text>

      {/* ITEMS */}
      {items.map((item, index) => (
        <Flex
          key={item.id}
          direction="column"
          gap="sm"
          style={{
            border: "1px solid #eee",
            borderRadius: "12px",
            padding: "12px",
            marginBottom: "8px",
          }}
        >
          {/* HEADER */}
          <Flex justify="space-between" align="center">
            <Text>{`${field.question || "Item"} ${index + 1}`}</Text>

            {index !== 0 && (
              <Button variant="link" color="primary" size="sm" onClick={() => handleDelete(item.id)}>
                <TrashIcon size={16} />
              </Button>
            )}
          </Flex>

          {/* FIELDS */}
          {field.sub_fields?.map((subField) => {
            if (subField.component === "composite") {
              return (
                <Flex key={subField.name} style={{ width: "100%" }}>
                  {subField.fields?.map((innerField, idx) => (
                    <div
                      key={innerField.name}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        marginRight: idx === 0 ? "8px" : "0px",
                        marginLeft: idx === 1 ? "8px" : "0px",
                      }}
                    >
                      {renderField({
                        fieldConfig: innerField,
                        value: item[innerField.name],
                        onChange: (val) => updateItem(item.id, innerField.name, val),
                        fieldIsRequired,
                        extraProps,
                      })}

                      {innerField.info_text && <div className="dfh-info-text">{innerField.info_text}</div>}
                    </div>
                  ))}
                </Flex>
              );
            }

            return (
              <div key={subField.name} style={{ marginBottom: "8px" }}>
                {renderField({
                  fieldConfig: subField,
                  value: item[subField.name],
                  onChange: (val) => updateItem(item.id, subField.name, val),
                  fieldIsRequired,
                  extraProps,
                })}
                {subField.info_text && <div className="dfh-info-text">{subField.info_text}</div>}
              </div>
            );
          })}
          {submitted &&
            errors?.[field.name]?.[index] &&
            errors[field.name][index].map((err, errIndex) => (
              <Message
                key={errIndex}
                style={{ width: "100%", marginTop: "8px" }}
                message={{
                  primary: err,
                }}
                variant="error"
                onClose={() => {}}
              />
            ))}
        </Flex>
      ))}

      {/* ADD BUTTON */}
      <Button
        variant="link"
        color="blue"
        size="sm"
        onClick={handleAdd}
        disabled={items.length >= maxItems}
        style={{ alignSelf: "center", marginTop: "8px" }}
      >
        <PlusIcon size={16} style={{ marginRight: "6px" }} />
        Add More
      </Button>

      {/* INFO TEXT */}
      {field.info_text && <div className="dfh-info-text">{field.info_text}</div>}
    </Flex>
  );
};

export default DynamicMultipleOptions;
